<!--
/**
 * TenantLogin — full port of apps/admin/src/views/TenantLogin.vue (Phase 2.2).
 *
 * Parity notes (step-1 inventory in the port report):
 * - API: TenantService.login(form) → POST /login with payload { email, password }.
 *   The form object handed to the service is a FormTypeV2 with a `send` method, so
 *   apiService's isForm() delegates to it exactly like the old Form.ts instance:
 *   same wire payload (old Form.toJson()), busy flag around the request, server
 *   validation errors (`error.response.data.errors`) surfaced under the fields.
 * - Validation (audit F2): old Vuelidate rules email{required,email},
 *   password{required,minLength(8)} all rendered `validation.general` (Form.errorMessage
 *   falls through to '' for every rule) — every zod rule below uses
 *   zMsg('validation.general'). Timing: errors appear on blur (old validate-on-blur +
 *   @blur validate), clear while typing (old `@input="delete form.$errors.x"`),
 *   full-form validation gates submit (old `if (!this.form.validate()) return`).
 * - Redirects: on success Store.set('redirect', false); push 'gettingStarted' when
 *   getstarted_status !== 'completed' — DEAD: no such route exists (old admin's router
 *   had none either); the rejection is swallowed by .catch, ported verbatim — then
 *   push 'userLogin' with query { initial: true } (old code never returned early,
 *   both pushes always ran).
 * - $snackbar: 'sync_exception' → general.error_contact_support (error, 10s);
 *   logo easter egg (10 clicks/5s window) → package.json version (info, 10s).
 * - Locale: country <select> (sr/me/ba + disabled placeholder) → Locale.set +
 *   Locale.setLanguage, no reload (i18n updates reactively, as in old admin).
 * - created(): TenantService/UserService token+tenant/user removal (fire-and-forget),
 *   branded logo via Locale.getBrandedLogoUrl('dark'), window.Locale debug global.
 * - Background graphics panel hidden on smAndDown (old $vuetify.breakpoint).
 * - Redesign P6.2: restyled onto the POS P01 auth pattern — always-dark island
 *   (local `dark` class + token surfaces: bg-default panel over bg-muted graphics
 *   canvas, the old hex palette and background.png/gradients dropped), 560px form
 *   column, USelectMenu country picker, solid-primary submit (was warning/amber),
 *   ramp typography, entrance animations removed (off-system motion).
 * - Divergences logged in the report: Enter on the email field is prevented (old
 *   no-op preserved); malformed error payloads (response without data.errors) no
 *   longer hang the login promise (old Form.setErrors threw on Object.keys(undefined)).
 */
-->
<template>
    <!-- POS P01 auth pattern (redesign P6.2): always-dark island — the local
         `dark` class re-scopes tokens; color is asserted on the panel (L4). -->
    <div class="tenant-login dark h-dvh overflow-auto font-sans">
        <div class="relative flex h-full min-h-160 justify-center bg-default text-default">
            <!-- Left graphics panel: the kit's AuthHero, as on the POS tenant login -->
            <div class="bg-graphics relative flex-7 border-r border-default bg-muted max-sm:hidden">
                <AuthHero />
            </div>

            <!-- Country switch (top-right, field-look select per P01) -->
            <div class="absolute top-10 right-9 z-10">
                <USelectMenu
                    :model-value="countryValue || undefined"
                    :items="countryItems"
                    value-key="value"
                    :search-input="false"
                    :placeholder="t('general.country')"
                    class="w-44"
                    @update:model-value="onCountryChange"
                />
            </div>

            <!-- Login form: fixed 560px column on desktop (P01) -->
            <UForm
                ref="loginForm"
                :schema="schema"
                :state="form.data"
                :validate-on="['blur']"
                class="form flex max-w-150 flex-3 flex-col justify-center px-9 py-11 sm:max-w-140 sm:flex-none sm:basis-140"
                @submit="login"
            >
                <div class="logo mb-5 mt-5 flex max-h-37.5 w-full cursor-pointer justify-center" @click="logoClick">
                    <img
                        :src="logoUrl"
                        alt=""
                        class="max-h-full max-w-47 select-none"
                        draggable="false"
                    >
                </div>

                <div class="form-wrapper flex flex-col">
                    <h3 class="text-screen-title mb-1">
                        {{ t('general.administration') }}
                    </h3>

                    <p class="mb-5 text-muted">
                        {{ !isDemo ? t('get_started.login_intro') : t('get_started.login_intro_demo') }}
                    </p>

                    <UFormField name="email" :label="t('general.email')" class="mt-5">
                        <UInput
                            :model-value="form.data.email"
                            autofocus
                            size="lg"
                            class="w-full"
                            @update:model-value="onEmailInput"
                        />
                    </UFormField>

                    <UFormField name="password" :label="t('general.password')" class="mt-4">
                        <UInput
                            v-model="form.data.password"
                            :type="showPassword ? 'text' : 'password'"
                            size="lg"
                            class="w-full"
                            @update:model-value="loginForm?.clear('password')"
                        >
                            <template #trailing>
                                <UButton
                                    variant="link"
                                    color="neutral"
                                    size="sm"
                                    :icon="showPassword ? 'i-lucide-eye' : 'i-lucide-eye-off'"
                                    :aria-label="showPassword ? 'Hide password' : 'Show password'"
                                    @click="showPassword = !showPassword"
                                />
                            </template>
                        </UInput>
                        <!-- The old Vuetify `counter` is deliberately not ported: a login
                             password has no length limit to count toward (it rendered a
                             bare "0" under the empty field). -->
                    </UFormField>

                    <UButton
                        type="submit"
                        color="primary"
                        variant="solid"
                        block
                        size="lg"
                        :disabled="busy"
                        :loading="busy"
                        class="mt-5 tracking-[1.2px]"
                    >
                        {{ t('general.log_in') }}
                    </UButton>
                </div>
            </UForm>

            <!-- Version label (bottom-right, dimmed helper per P01) -->
            <div v-if="version" class="text-helper absolute bottom-2.5 right-2.5 text-right text-dimmed">
                <div>{{ locale === 'sr' ? 'CodeUX' : (locale === 'en' ? 'Codeus' : 'OllieTech') }} / Konty POS Version {{ version }}</div>
                <div v-if="environmentLabel">{{ environmentLabel }}</div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { z } from 'zod'
import { TenantService, UserService, Locale, Store, Settings, I18n, apiService } from 'app-core'
import type { FormTypeV2, ITenant, ITenantLoginRequest, LocaleType } from 'app-core'
import type { Form } from '@nuxt/ui'
import { AuthHero, useForm, zMsg } from 'app-components'
import { useToast } from '@/composables/useToast'
import { useLocale } from '@/composables/useLocale'
import packageJson from '../../package.json'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const { locale } = useLocale()

// ——— created() ———————————————————————————————————————————————————————————————
// Clear any stale tenant/user session — fire-and-forget, same as old created().
TenantService.removeToken()
TenantService.removeTenant()
UserService.removeToken()
UserService.removeUser()

const logoUrl = ref(Locale.getBrandedLogoUrl('dark'))

// Old debug global (`window.Locale = Locale`) kept for console parity; Window has no
// declared member for it, hence the structural widening (not declared app-wide on
// purpose — it is a TenantLogin-only legacy global).
;(window as Window & { Locale?: typeof Locale }).Locale = Locale

// ——— data ————————————————————————————————————————————————————————————————————
/**
 * Old Vuelidate `validations()`: email { required, email }, password { required,
 * minLength(8) }. Every rule uses zMsg('validation.general') — what old users actually
 * saw for ALL Vuelidate rules (Form.errorMessage fall-through, audit F2). Do not map
 * to specific keys.
 */
const schema = z.object({
    email: z.email({ error: zMsg('validation.general') }),
    password: z.string({ error: zMsg('validation.general') })
        .min(8, { error: zMsg('validation.general') })
})

// Old: new Form(this, 'form', { email: null, password: null })
const form = useForm(schema, { email: '', password: '' })
const busy = form.busy

const loginForm = useTemplateRef<Form<typeof schema>>('loginForm')

const showPassword = ref(false)
const isDemo = import.meta.env.VITE_ENV === 'staging'

let logoClickCount = 0
let logoClickTimeout: ReturnType<typeof setTimeout> | undefined

// ——— computed ————————————————————————————————————————————————————————————————
const version = computed(() =>
    I18n.c('general.certifiedAppId')
        ? I18n.c('general.certifiedAppId') + '/' + Settings.certifiedVersion
        : Settings.version
)

// Old: VUE_APP_ENV / NODE_ENV — visible only when BOTH are non-production, i.e. only
// on the dev server (.env.staging.local set NODE_ENV=production). Vite equivalents:
// VITE_ENV and the PROD build flag.
const environmentLabel = computed(() => {
    const nodeEnv = import.meta.env.PROD ? 'production' : 'development'
    if (import.meta.env.VITE_ENV !== 'production' && nodeEnv !== 'production') {
        return import.meta.env.VITE_ENV + ' / ' + nodeEnv
    }

    return ''
})

/** Old: per-option `:selected` bindings — placeholder when locale is not me/sr/ba. */
const countryValue = computed(() =>
    ['me', 'sr', 'ba'].includes(locale.value) ? locale.value : '')

const countryItems = computed(() => [
    { label: t('locales.sr'), value: 'sr' },
    { label: t('locales.me'), value: 'me' },
    { label: t('locales.ba'), value: 'ba' }
])

// ——— methods —————————————————————————————————————————————————————————————————
/**
 * Old `new Form(...)` stand-in handed to TenantService.login: a FormTypeV2 carrying a
 * `send` method, so apiService.post('/login', form) detects it (isForm) and delegates —
 * keeping the wire payload exactly `{ email, password }` (old Form.toJson()), the busy
 * flag around the request, and server validation errors under the fields (old
 * Form.send → setErrors; the old errorMessage() translation table fell through to ''
 * for every code, so raw server messages were shown — same here).
 * TenantService.offlineLogin writes `$errors` straight onto this object and throws;
 * login()'s catch surfaces those too, like the old reactive Form binding did.
 */
function makeLoginForm(): FormTypeV2<ITenantLoginRequest> {
    const loginPayload = {
        email: form.data.email,
        password: form.data.password,
        $busy: false,
        $errors: {} as Record<string, string[]>,
        $v: null,
        async send<T>(method: 'post' | 'put' | 'patch', uri: string, config?: Parameters<typeof apiService.http.post>[2]) {
            // old Form.startProcessing()
            busy.value = true
            loginPayload.$errors = {}
            loginForm.value?.clear()

            try {
                const response = await apiService.http[method]<T>(
                    uri,
                    { email: loginPayload.email, password: loginPayload.password },
                    config
                )
                busy.value = false // old Form.finishProcessing()
                return response
            } catch (errors) {
                busy.value = false // old Form.finishProcessing()

                const serverError = errors as { response?: { data?: { errors?: Record<string, string[]> } } }
                if (serverError.response?.data?.errors) {
                    loginPayload.$errors = serverError.response.data.errors
                    displayFieldErrors(loginPayload.$errors)
                }

                throw errors
            }
        }
    }

    return loginPayload
}

/** Old Form.$errors display: `{ field: [messages] }` rendered under each field. */
function displayFieldErrors(errors: Record<string, string[]>) {
    loginForm.value?.setErrors(
        Object.entries(errors).flatMap(([name, messages]) =>
            messages.map(message => ({ name, message })))
    )
}

/**
 * Login Tenant — old login(). The leading `if (!this.form.validate()) return` is
 * covered by UForm's schema validation: @submit only fires when valid, with the same
 * rules and messages shown under the fields.
 */
function login() {
    const loginPayload = makeLoginForm()

    TenantService.login(loginPayload).then(response => {
        Store.set('redirect', false)

        // Redirect to getting started if it hasn't been completed yet.
        // DEAD BEHAVIOR PORTED VERBATIM: no 'gettingStarted' route exists (none existed
        // in old admin's router either). vue-router 5 THROWS synchronously on an unknown
        // name (v3 only warned), and the throw happens before the promise exists so the
        // trailing .catch can't attach — it would propagate into login()'s catch and
        // silently abort the userLogin push below. Guard with hasRoute (same fix as
        // AppNavbar.goToKitchen) to keep the dead push dead while userLogin always runs.
        if (response.data.tenant.getstarted_status !== 'completed' && router.hasRoute('gettingStarted')) {
            router.push({ name: 'gettingStarted' }).catch(() => {})
        }

        // Go to User Login (old code always ran this push — no early return above)
        router.push({ name: 'userLogin', query: { initial: 'true' } }).catch(() => {})
    }).catch((error: unknown) => {
        // Offline login failure: TenantService.offlineLogin assigned $errors directly
        // to the form object (old: displayed reactively under the email field).
        if (Object.keys(loginPayload.$errors).length) {
            displayFieldErrors(loginPayload.$errors)
        }

        if ((error as { message?: string } | null)?.message === 'sync_exception') {
            toast.add({ description: t('general.error_contact_support'), color: 'error', duration: 10000 })
        } else if ((error as { response?: { status?: number } } | null)?.response?.status === 402) {
            // aria-api signs an inactive tenant in but refuses its data — say why the login stops.
            // The 402 body carries the tenant's `status`, the only field inactiveMessageKey reads.
            const refusal = (error as { response: { data?: Pick<ITenant, 'status'> } }).response.data
            toast.add({ description: t(TenantService.inactiveMessageKey(refusal as ITenant)), color: 'error', duration: 10000 })
        }
    })
}

/** Old: clears the field's error while typing (`@input="delete form.$errors.email"`). */
function onEmailInput(value: string | number) {
    // old v-model.trim
    form.data.email = String(value).trim()
    loginForm.value?.clear('email')
}

/** Version easter egg — 10 logo clicks within a rolling 5s window. */
function logoClick() {
    logoClickCount++
    if (logoClickCount === 10) {
        toast.add({ description: packageJson.version, color: 'info', duration: 10000 })
        logoClickCount = 0
        clearTimeout(logoClickTimeout)
        return
    }

    clearTimeout(logoClickTimeout)
    logoClickTimeout = setTimeout(() => {
        logoClickCount = 0
    }, 5000)
}

async function onCountryChange(value: LocaleType) {
    await Locale.set(value)
    await Locale.setLanguage(value)
}
</script>

<style scoped>
/* Dark-panel autofill correction for the login inputs (token-driven — the
 * local dark scope resolves these to the dark palette). */
.form :deep(input:-webkit-autofill),
.form :deep(input:-webkit-autofill:hover),
.form :deep(input:-webkit-autofill:focus) {
    -webkit-box-shadow: 0 0 0 1000px var(--ui-bg-accented) inset !important;
    -webkit-text-fill-color: var(--ui-text-highlighted) !important;
    caret-color: var(--ui-text-highlighted);
}
</style>
